import type { Base } from "./_config/index.js"
import { Config } from "./_config/index.js"
import { Transaction } from "./ethereum/Models/Transaction.js"
import { WalletState } from "./ethereum/Models/WalletState.js"
import { type ConnectEthereumUseCaseInput } from "./ethereum/UseCases/ConnectEthereumUseCase.js"
import { type WaitForTransactionEthereumUseCaseInput } from "./ethereum/UseCases/WaitForTransactionEthereumUseCase.js"

import { MigrationInfo } from "./migration/Models/MigrationInfo.js"
import {
  WithdrawYieldMigrationUseCaseInput,
  WithdrawYieldMigrationUseCaseOutput,
} from "./migration/UseCases/WithdrawYieldMigrationUseCase.js"
import {
  BridgeTokensMigrationUseCaseInput,
  BridgeTokensMigrationUseCaseOutput,
} from "./migration/UseCases/BridgeTokensMigrationUseCase.js"

import { GetTransactionsEthereumUseCaseOutput } from "./ethereum/UseCases/GetTransactionsEthereumUseCase.js"
import { CleanTransactionsEthereumUseCaseOutput } from "./ethereum/UseCases/CleanTransactionsEthereumUseCase.js"

export class Domain {
  #config: Config

  static create(config: Base) {
    return new Domain(config)
  }

  constructor(config: Base) {
    this.#config = Config.create(config)
  }

  get config() {
    return this.#config
  }

  /** Ethereum */
  get InitPublicClientEthereumUseCase() { return this.#getter<void, void>(async () => import("./ethereum/UseCases/InitPublicClientEthereumUseCase.js"), "InitPublicClientEthereumUseCase") } // prettier-ignore
  get ConnectEthereumUseCase() { return this.#getter<ConnectEthereumUseCaseInput, WalletState>(async () => import("./ethereum/UseCases/ConnectEthereumUseCase.js"), "ConnectEthereumUseCase") } // prettier-ignore
  get DisconnectEthereumUseCase() { return this.#getter<void, WalletState>(async () => import("./ethereum/UseCases/DisconnectEthereumUseCase.js"), "DisconnectEthereumUseCase") } // prettier-ignore
  get GetWalletStateEthereumUseCase() { return this.#getter<void, WalletState>(async () => import("./ethereum/UseCases/GetWalletStateEthereumUseCase.js"), "GetWalletStateEthereumUseCase") } // prettier-ignore
  get SwitchChainEthereumUseCase() { return this.#getter<void, WalletState>(async () => import("./ethereum/UseCases/SwitchChainEthereumUseCase.js"), "SwitchChainEthereumUseCase") } // prettier-ignore
  get ReconnectEthereumUseCase() { return this.#getter<void, WalletState>(async () => import("./ethereum/UseCases/ReconnectEthereumUseCase.js"), "ReconnectEthereumUseCase") } // prettier-ignore
  get GetTransactionsEthereumUseCase() {
    return this.#getter<void, GetTransactionsEthereumUseCaseOutput>(
      async () => import("./ethereum/UseCases/GetTransactionsEthereumUseCase.js"),
      "GetTransactionsEthereumUseCase",
    )
  } // prettier-ignore
  get WaitForTransactionEthereumUseCase() { return this.#getter<WaitForTransactionEthereumUseCaseInput, Transaction>(async () => import("./ethereum/UseCases/WaitForTransactionEthereumUseCase.js"), "WaitForTransactionEthereumUseCase") } // prettier-ignore
  get CleanTransactionsEthereumUseCase() { return this.#getter<void, CleanTransactionsEthereumUseCaseOutput>(async () => import("./ethereum/UseCases/CleanTransactionsEthereumUseCase.js"), "CleanTransactionsEthereumUseCase") } // prettier-ignore

  /** Migration */
  get GetMigrationInfoUseCase() { return this.#getter<void, MigrationInfo>(async () => import("./migration/UseCases/GetMigrationInfoUseCase.js"), "GetMigrationInfoUseCase") } // prettier-ignore
  get WithdrawYieldMigrationUseCase() { return this.#getter<WithdrawYieldMigrationUseCaseInput, WithdrawYieldMigrationUseCaseOutput>(async () => import("./migration/UseCases/WithdrawYieldMigrationUseCase.js"), "WithdrawYieldMigrationUseCase") } // prettier-ignore
  get BridgeTokensMigrationUseCase() { return this.#getter<BridgeTokensMigrationUseCaseInput, BridgeTokensMigrationUseCaseOutput>(async () => import("./migration/UseCases/BridgeTokensMigrationUseCase.js"), "BridgeTokensMigrationUseCase") } // prettier-ignore

  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  #getter<I, O>(loader: Function, name: string) {
    return {
      execute: async (input: I): Promise<O> => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        const Klass = await loader().then((mod) => mod[name])
        const uc = Klass.create({ config: this.#config })

        return uc.execute(input) as O
      },
    }
  }
}
